<template>
  <div>
    <label
      v-if="displayLabel"
      :for="id"
      class="block text-sm font-medium text-gray-700"
    >
      {{ displayLabel }}
    </label>

    <AutoComplete
      :id="id"
      v-model="selection"
      :suggestions="filteredStudents"
      @complete="searchStudents"
      @clear="handleClear"
      optionLabel="fullName"
      :placeholder="displayPlaceholder"
      class="mt-1 !w-full"
      inputClass="w-full"
    >
      <template #option="{ option }">
        <div>
          {{ `${(option as Student).first_name} ${(option as Student).surname ?? ''}`.trim() }}
          <span v-if="(option as Student).matric_no" class="text-gray-500 text-sm ml-1">({{ (option as Student).matric_no }})</span>
        </div>
      </template>
    </AutoComplete>
  </div>
</template>

<script setup lang="ts">
import { useStudentSearch } from '@/composables/useStudentSearch';
import type { Student, StudentFilter } from '@/types/composition-functions';
import { useSchoolLabels } from '@/composables/useSchoolLabels';

const { labels } = useSchoolLabels();

interface Props {
  modelValue: Student | null;
  label?: string;
  placeholder?: string;
  id?: string;
  query?: unknown;
  filter?: Partial<StudentFilter>;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  label: undefined,
  placeholder: undefined,
  id: 'student-search-input',
});

const displayLabel = computed(() => props.label || `Search ${labels.value.STUDENT}`);
const displayPlaceholder = computed(() => props.placeholder || `Search by name or ${labels.value.STUDENT.toLowerCase()} ID`);

const emit = defineEmits<{
  (e: 'update:modelValue', value: Student | null): void
  (e: 'select', value: Student): void
}>();

const { filteredStudents, searchStudents } = useStudentSearch({
  query: props.query as import('graphql').DocumentNode | undefined,
  filter: props.filter,
});

// Bind AutoComplete v-model directly to the selected Student object via a
// computed proxy over the prop. Avoids the prior "string ref but PrimeVue
// momentarily writes the option object" type-flip that intermittently swallowed
// selections in PrimeVue 4.
const selection = computed<Student | null | string>({
  get: () => props.modelValue,
  set: (value) => {
    // PrimeVue AutoComplete sets v-model to the typed string while the user
    // types, then to the selected option object on pick. Only treat object
    // writes as a real selection.
    if (value && typeof value === 'object') {
      emit('update:modelValue', value as Student);
      emit('select', value as Student);
    } else if (value === null || value === '') {
      emit('update:modelValue', null);
    }
    // Ignore intermediate string-typing writes — they shouldn't clear the parent's selection.
  },
});

const handleClear = () => {
  filteredStudents.value = [];
  emit('update:modelValue', null);
};
</script>

<style scoped>
.p-autocomplete .p-autocomplete-loader {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
}
</style>
